import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { acceptProjectInvitation } from '@/lib/api/projectMembers'
import { useAuth } from '@/contexts/AuthContext'
import { Loader2, CheckCircle2, XCircle, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'needs_auth'>('loading')
  const [error, setError] = useState<string>('')
  const [_projectId, setProjectId] = useState<string>('')

  useEffect(() => {
    const accept = async () => {
      if (!token) {
        setStatus('error')
        setError('Invalid invitation link')
        return
      }

      // Wait for auth to finish loading
      if (authLoading) {
        return
      }

      // If user is not authenticated, show sign up/login prompt
      if (!user) {
        // Store the invitation token in sessionStorage so we can accept it after login
        sessionStorage.setItem('pending_invitation_token', token)
        setStatus('needs_auth')
        return
      }

      try {
        const returnedProjectId = await acceptProjectInvitation(token)
        setProjectId(returnedProjectId)
        setStatus('success')

        // Clear the pending invitation token
        sessionStorage.removeItem('pending_invitation_token')

        // Redirect to project after 2 seconds
        setTimeout(() => {
          navigate(`/projects/${returnedProjectId}`)
        }, 2000)
      } catch (err: any) {
        setStatus('error')
        setError(err.message || 'Failed to accept invitation')
      }
    }

    accept()
  }, [token, navigate, user, authLoading])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-purple-600" />
            <h2 className="text-2xl font-semibold mb-2">Accepting Invitation</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we add you to the project...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-green-600 dark:text-green-400">
              Invitation Accepted!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You've been successfully added to the project.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Redirecting to project...
            </p>
          </div>
        )}

        {status === 'needs_auth' && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <UserPlus className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-purple-600 dark:text-purple-400">
              Sign In Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You need to sign in or create an account to accept this invitation.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/signup')}
                className="w-full"
              >
                Create Account
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Sign In
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
              After signing in, you'll be automatically added to the project.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-red-600 dark:text-red-400">
              Invitation Failed
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <Button
              onClick={() => navigate('/projects')}
              variant="outline"
            >
              Go to Projects
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
